<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to a molecule
import { computed, ref, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import OverlayPanel from 'primevue/overlaypanel';
import type { ProductAtCart } from '@ProductsAndServicesModule';
import type { Discount } from '../../../interfaces';
import { useAToast } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import { ApplyRetailPresetDiscountService, RemoveRetailPresetDiscountService } from '../../../services';
import useShoppingCartStore from '../../../store';

withDefaults(
  defineProps<{
    disabled?: boolean;
    isEditingProducts?: boolean;
    label?: string;
  }>(),
  {
    disabled: false,
    label: 'cart.summary.promoProducts',
  }
);

const store = useShoppingCartStore();
const { cartData, discounts } = storeToRefs(store);

const { notify, TOAST_TYPES } = useAToast();

const TOAST_ERROR = {
  message: 'cart.toast.medicalCosts.error.message',
  title: 'cart.toast.medicalCosts.error.title',
  type: TOAST_TYPES.ERROR,
};

const promoBlockedOverlay = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('promoBlockedOverlay');

const isOpen = ref(false);

const retailPresetCode = computed(() => {
  const products = cartData.value?.products ?? [];
  const services = cartData.value?.services ?? [];
  const code = cartData.value?.retail_preset_code ?? null;
  if (code) return code;

  const firstWithCode =
    products.find(({ discount_code }: ProductAtCart) => discount_code) ??
    services.find(({ discount_code }: ProductAtCart) => discount_code);

  return firstWithCode?.discount_code ?? null;
});

const selectedDiscount = computed<Discount | undefined>(() =>
  discounts.value.find(({ code }: Discount) => code === retailPresetCode.value)
);

const selectedCodeValue = computed<number>(() => selectedDiscount.value?.amount ?? 0);

const isPercentDiscount = computed(() => selectedDiscount.value?.discountType === 'percent');

const discountDisplay = computed(() => {
  const value = selectedCodeValue.value;
  if (value <= 0) return '';
  return isPercentDiscount.value ? `${value}%` : applyShelterCurrency(value).replace(/\$([\d,.]+)/, '$($1)');
});

const selectedCode = computed({
  get: () => retailPresetCode.value,
  set: (newCode: string | null) => {
    const oldCode = retailPresetCode.value;

    const isRemoving = oldCode && !newCode;
    if (isRemoving && oldCode) {
      RemoveRetailPresetDiscountService({ code: oldCode })
        .then(({ data }) => (cartData.value = data))
        .catch(() => notify(TOAST_ERROR));
      return;
    }

    if (!newCode) return;

    ApplyRetailPresetDiscountService({ code: newCode })
      .then(({ data }) => (cartData.value = data))
      .catch((err: any) => {
        const status = err?.response?.status;
        const msg = err?.response?.data?.error;

        // TODO :: TASK-2023 :: Check this
        if (status === 409) {
          notify({
            message: msg || 'cart.summary.promoBlockedByCustomDiscount',
            title: 'cart.toast.medicalCosts.error.title',
            type: TOAST_TYPES.ERROR,
          });
        } else {
          notify(TOAST_ERROR);
        }
      });
  },
});

function onTogglePromoBlockedOverlay(event: Event) {
  promoBlockedOverlay.value?.toggle(event);
}
</script>

<template>
  <div class="flex items-center justify-between gap-2">
    <template v-if="isEditingProducts">
      <div class="relative flex items-center gap-1">
        <label
          v-if="retailPresetCode"
          class="absolute -top-3.5 left-0 z-10 text-xs text-gray-500"
        >
          {{ $t(label) }}
        </label>

        <div :class="[{ 'cursor-help': disabled }, 'relative']">
          <AFieldDropdown
            v-model="selectedCode"
            v-model:is-open="isOpen"
            class="pt h-8 w-fit"
            customClass="!h-8"
            customInputStyle="h-8 pr-10"
            id="retail-promo"
            optionLabel="code"
            optionValue="code"
            :disabled
            :options="discounts"
            :placeholder="$t('cart.label.selectDiscount')"
          />

          <div
            v-if="disabled"
            class="absolute inset-0"
            @click="onTogglePromoBlockedOverlay($event)"
            @mouseenter="onTogglePromoBlockedOverlay($event)"
            @mouseleave="onTogglePromoBlockedOverlay($event)"
          />

          <OverlayPanel
            v-if="disabled"
            class="max-w-[280px] text-sm"
            ref="promoBlockedOverlay"
          >
            <p class="text-surface-700 dark:text-surface-300 p-0">
              {{ $t('cart.summary.promoBlockedByCustomDiscount') }}
            </p>
          </OverlayPanel>
        </div>
      </div>
    </template>
    <div
      v-else-if="retailPresetCode"
      class="flex items-center justify-between gap-2"
    >
      <span class="text-xs text-gray-500"> {{ $t('cart.summary.promo') }}: {{ retailPresetCode }} </span>
      <span
        v-if="discountDisplay"
        class="text-sm font-medium text-red-600"
      >
        {{ discountDisplay }}
      </span>
    </div>
  </div>
</template>
